import { ActionType, ProColumns } from '@ant-design/pro-components';
import { DatePicker, Form, Input } from 'antd';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomButton from 'base/ui/components/Button';
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { CheckStatus, CloseStatus } from 'base/ui/components/Icons';
import dayjs from 'dayjs';
import usePartialState from 'hooks/usePartialState';
import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react';
import PageWrapper from 'pages/Components/PageWrapper';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { SearchModel } from 'pages/model';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonModel } from 'services/Main/common_services';
import { WarrantyTimeRemindModel } from 'services/Masterdata/warrantyRemind_services';
import { commonPresenter, commonStore, warrantyRemindPresenter, warrantyRemindStore } from 'stores/root_store';
import { DATE_FORMAT_POST, PAGE_SIZE_DEFAULT } from 'utils/constants';


const { RangePicker } = DatePicker;

// Bỏ theo dõi nhắc nhở bảo hành
export type TableListItem = {
  key: number;
  stt: number;
  profileCode: string;
};

const statusList = [
  {
    label: 'Đã gửi',
    value: true
  },
  {
    label: 'Chưa gửi',
    value: false
  },
]

const createWarrantyTimeRemind = () => {
  return observer(() => {
    const [form] = Form.useForm();
    const tableRef = useRef<ActionType>();

    //=====================================CHUYỂN NGÔN NGỮ=====================================//
    const { t } = useTranslation();

    const defaultColumn: ProColumns[] = [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 50,
        align: 'center',
      },
      // Ngày nhắc nhở
      {
        title: 'Ngày nhắc nhở',
        dataIndex: 'remindDate',
        valueType: 'dateTime',
        fieldProps: {
          format: 'DD/MM/YYYY',
        },
        width: 150,
        onCell: (_, index) => ({
          className: 'text-center font-bold',
        }),
      },
      // Mã phiếu ĐKBH
      {
        title: 'Mã phiếu ĐKBH',
        dataIndex: 'registerCode',
        align: 'center',
      },
      // Mã cấu hình
      {
        title: 'Mã cấu hình',
        dataIndex: 'remindConfigCode',
        align: 'center',
      },
      // Mã khách hàng
      {
        title: 'Mã khách hàng',
        dataIndex: 'profileCode',
        align: 'center',
      },
      // Tên khách hàng
      {
        title: 'Tên khách hàng',
        dataIndex: 'profileName',
        align: 'center',
      },
      // Trạng thái gửi
      {
        title: 'Trạng thái gửi',
        dataIndex: 'status',
        align: 'center',
        render: (_, record) =>
          record.status ? (
            <CheckStatus width="14" className="!fill-[#0052cc] block mx-auto" />
          ) : (
            <CloseStatus width="14" className="!fill-red-500 block mx-auto" />
          ),
      },
      // Message
      {
        title: 'Message',
        dataIndex: 'message',
      },
    ];

    //--- DATA TABLE ------------------------------------------------------------
    const [dataPaging, setDataPaging] = useState<SearchModel>({
      paging: {
        pageSize: PAGE_SIZE_DEFAULT,
        pageIndex: 1,
        orderBy: undefined,
        orderByDesc: undefined,
      },
    });


    // data common date
    const [listCommonDate, setListCommonDate] = useState<CommonModel[]>([]);
    // disable rangePicker khi chọn ngày từ common
    const [disableRangePicker, setDisableRangePicker] = useState(false);

    const [loading, setLoading] = usePartialState({
      loadingSearch: false,
      loading: false,
    })

    // data table
    const [dataTable, setDataTable] = useState<WarrantyTimeRemindModel[]>([]);

    // =================================================================================================================
    // =================================================================================================================
    // =================================================================================================================
    const getDataTable = async (dataSearch: any) => {
      await warrantyRemindPresenter.warrantyTimeRemind(warrantyRemindStore, dataSearch);
      if (warrantyRemindStore.warrantyTimeRemind.isSuccess) {
        const dataTemp = cloneDeep(warrantyRemindStore.warrantyTimeRemind.data);
        setDataTable(dataTemp);
        return dataTemp;
      }
      setDataTable([]);
      return [];
    };


    // Lấy ngày bằng common data
    const getDateByKey = async (key: string) => {
      setLoading({ loadingSearch: true })
      await commonPresenter.getDateByCommonDate(commonStore, key);
      if (commonStore.commonDate.fromDate) {
        setDataPaging({
          ...dataPaging,
          fromTime: commonStore.commonDate.fromDate ? dayjs(commonStore.commonDate.fromDate)?.format(DATE_FORMAT_POST) : undefined,
          toTime: commonStore.commonDate.toDate ? dayjs(commonStore.commonDate.toDate)?.format(DATE_FORMAT_POST) : undefined,
        });

        form.setFieldValue("ngayNN", [
          dayjs(commonStore.commonDate.fromDate),
          dayjs(commonStore.commonDate.toDate),
        ]);


      } else {
        form.setFieldValue("ngayNN", undefined);
      }
      setLoading({ loadingSearch: false })
    };

    // Lấy danh sách common date
    const getListCommonDate = async () => {
      setLoading({ loadingSearch: true })
      await commonPresenter.getListSelectCommonDate(commonStore);
      if (commonStore.commonRespone.isSuccess) {
        setListCommonDate(commonStore.commonRespone.data);
      }
      setLoading({ loadingSearch: false })
    };

    useEffect(() => {
      getListCommonDate()
    }, [])




    return (
      <PageWrapper
        breadcrumb={[{
          text: 'Nhắc nhở thời gian lịch bảo hành'
        }]}
      >
        <BoxContainer>
          <SearchForm initialValues={{ actived: true }} form={form} column="col-3"
            onFinish={async (value) => {
              setDataPaging({
                paging: {
                  pageSize: PAGE_SIZE_DEFAULT,
                  pageIndex: 1,
                },
                ...Object.fromEntries(Object.entries(value).filter(([_, e]) => {
                  if (typeof e === 'boolean') {
                    return true
                  } else if (Array.isArray(e) && e.length === 0) {
                    return false
                  }
                  else {
                    return !!e
                  }
                })),
                fromTime: value.ngayNN ? dayjs(value.ngayNN[0]).format(DATE_FORMAT_POST) : null,
                toTime: value.ngayNN ? dayjs(value.ngayNN[1]).format(DATE_FORMAT_POST) : null,
              })
              tableRef.current?.reload()
            }
            }
          >
            {/* Ngày nhắc nhở */}
            <SearchItem label={'Ngày nhắc nhở'} name={'commonDate'}>
              <CustomSelect
                options={listCommonDate}
                fieldNames={{ label: "value", value: "key" }}
                onChange={async (value, option) => {
                  if (option) {
                    setDisableRangePicker(
                      option.key === "Custom" ? false : true
                    );
                    await getDateByKey(option.key);
                  } else {
                    setDisableRangePicker(false);
                    form.setFieldValue("ngayNN", undefined);
                  }
                }}
              />
            </SearchItem>
            {/* chọn ngày*/}
            <SearchItem label={' '} name={"ngayNN"} col2>
              <RangePicker
                disabled={disableRangePicker}
                format="DD/MM/YYYY"
                className="w-full"
              />
            </SearchItem>
            {/* Mã phiếu ĐKBH */}
            <SearchItem label={'Mã phiếu ĐKBH'} name={'registerCode'}>
              <Input />
            </SearchItem>

            {/* Trạng thái gửi tin */}
            <SearchItem
              name={"status"}
              label={'Trạng thái gửi'}
            >
              <CustomSelect
                allowClear
                options={statusList}
                showSearch
                filterOption={(input, option) =>
                  (option?.value ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </SearchItem>

            <div className='col-span-2 mt-5 flex justify-center items-center'>
              <CustomButton type='primary' onClick={() => {
                form.submit()
              }}>
                Tìm kiếm
              </CustomButton>
            </div>
          </SearchForm>

          <CustomProTable
            columns={defaultColumn}
            rowKey="stt"
            size="small"
            dataSource={dataTable}
            search={false}
            actionRef={tableRef}
            request={async (params = {}, sort, filter) => {
              const data = await getDataTable({
                ...dataPaging,
              });
              return {
                data: data,
                success: warrantyRemindStore.warrantyTimeRemind.isSuccess,
              };
            }}

            pagination={{
              ...tablePagination(dataPaging, setDataPaging),
              total: warrantyRemindStore.warrantyTimeRemind.paging?.totalCount,
            }}
          />
        </BoxContainer>
      </PageWrapper>
    );
  });
}

export default createWarrantyTimeRemind